import * as Sentry from '@sentry/react';
import {
  FieldPasswordCompact,
  FieldTextCompact,
  IsolateMain,
  Pushbutton
} from '@stratumn/atomic';
import to from 'await-to-js';
import LoadingState from 'components/organization/loadingState';
import { Box, Divider, Logo, OauthOptions, SignupStepper } from 'components/ui';
import { ROUTE_GENERATE_KEYS, ROUTE_LOGIN } from 'constant/routes';
import compose from 'lodash.flowright';
import qs from 'query-string';
import type React from 'react';
import { type ChangeEvent, useEffect, useState } from 'react';
import injectSheet from 'react-jss';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { SpanType, withSpanAsync } from 'tracing';
import { isBadRequestError } from 'utils/fetcher';
import { withRestClient } from 'wrappers';

import { AUTH_PROVIDER_STRATUMN } from '../../constant/types';
import styles from './conduentSignUp.style';

interface Client {
  checkInvite: (
    invitationToken: string | string[] | null
  ) => Promise<{ providers: string[] }>;
  signupConduent: (
    firstName: string,
    lastName: string,
    username: string,
    password: string,
    invitationToken: string | string[] | null
  ) => Promise<void>;
}

interface SignupProps {
  classes: Record<string, string>;
  client: Client;
}

export const Signup: React.FC<SignupProps> = ({ classes, client }) => {
  const location = useLocation();
  const history = useHistory();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState<string[] | null>(null);

  const togglePassword = () => setShowPassword(!showPassword);

  useEffect(() => {
    const fetchInvite = async () => {
      const { invitationToken } = qs.parse(location.search);

      try {
        const { providers } = await client.checkInvite(invitationToken);
        setProviders(providers);
        setLoading(false);
      } catch (e) {
        console.error(e);
        toast.error(
          e instanceof Error
            ? e.message
            : 'An error occurred while checking invitation'
        );
        setLoading(false);
      }
    };

    fetchInvite();
  }, [location.search, client]);

  const isValid = () =>
    !submitted && !!firstName && !!lastName && !!username && !!password;

  const signupConduent = async () =>
    withSpanAsync('signupConduent', SpanType.processing, async () => {
      const { invitationToken } = qs.parse(location.search);
      setSubmitted(true);
      const [err] = await to(
        client.signupConduent(
          firstName,
          lastName,
          username,
          password,
          invitationToken
        )
      );
      // client is responsible for error handling however errors in Promise
      // have to be handled hence the return;
      if (err) {
        if (isBadRequestError(err)) {
          toast.error(err.message);
          setSubmitted(false);
          return;
        }
        Sentry.captureException(err);
        toast.error(
          `The signup through Siebel failed.\n ${
            err instanceof Error ? err.message : 'An error occurred'
          }`
        );
        setSubmitted(false);
        return;
      }

      history.push(ROUTE_GENERATE_KEYS);
    });

  const renderStratumnProvider = () => {
    if (providers && !providers.includes(AUTH_PROVIDER_STRATUMN)) return null;

    return (
      <>
        <div className={classes.names}>
          <div className={classes.name} data-cy="first-name">
            <FieldTextCompact
              data-testid="input-firstName"
              label="First name"
              onValueChange={(event: ChangeEvent<HTMLInputElement>) =>
                setFirstName(event.target.value)
              }
              value={firstName}
            />
          </div>
          <div className={classes.name} data-cy="last-name">
            <FieldTextCompact
              data-testid="input-lastName"
              label="Last name"
              onValueChange={(event: ChangeEvent<HTMLInputElement>) =>
                setLastName(event.target.value)
              }
              value={lastName}
            />
          </div>
        </div>
        <div className={classes.info} data-cy="username">
          <FieldTextCompact
            data-testid="input-username"
            label="Siebel Username"
            onValueChange={(event: ChangeEvent<HTMLInputElement>) =>
              setUsername(event.target.value)
            }
            value={username}
          />
        </div>
        <div className={classes.info} data-cy="password">
          <FieldPasswordCompact
            data-testid="input-password"
            label="Siebel Password"
            onValueChange={(event: ChangeEvent<HTMLInputElement>) =>
              setPassword(event.target.value)
            }
            onVisibilityChange={togglePassword}
            passwordIsVisible={showPassword}
            value={password}
          />
        </div>
        <div className={classes.signupContainer}>
          <div>
            <Pushbutton
              data-testid="signup-button"
              disabled={!isValid()}
              onClick={signupConduent}
              primary
            >
              Create ID
            </Pushbutton>
          </div>
        </div>
      </>
    );
  };

  const renderDivider = () => {
    if (!providers) {
      return <Divider text="Or sign up with" />;
    }
    if (providers.includes(AUTH_PROVIDER_STRATUMN)) {
      if (providers.length === 1) {
        return null;
      }
      return <Divider text="Or sign up with" />;
    }
    return <Divider text="Sign up with" />;
  };

  if (loading) return <LoadingState fullHeight />;

  return (
    <IsolateMain>
      <SignupStepper step={0} />
      <div className={classes.logoWrapper}>
        <Logo />
      </div>
      <Box
        secondaryTitle={
          <div className={classes.signIn}>
            Already have a Stratumn Account? <a href={ROUTE_LOGIN}>Log in</a>.
          </div>
        }
      >
        {renderStratumnProvider()}
        {renderDivider()}
        <OauthOptions providers={providers} />
      </Box>
    </IsolateMain>
  );
};

export default compose(withRestClient, injectSheet(styles))(Signup);
